/** Attachments (spec05). */

/** FR3: per-file limit. */
export const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;
/** FR5: files no note refers to are deleted after this many days. */
export const ATTACHMENT_RETENTION_DAYS = 30;
export const MAX_ATTACHMENT_NAME_LENGTH = 200;

/** FR2: image types shown inline. Everything else (SVG included) is download only. */
export const INLINE_IMAGE_TYPES: readonly string[] = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
];

export const isInlineImage = (mimeType: string) => INLINE_IMAGE_TYPES.includes(mimeType);

export const attachmentPath = (id: string) => `/api/attachments/${id}`;

/**
 * A file name safe to store and send back: no path parts, control characters, leading dots
 * or Markdown link brackets; at most 200 characters (the extension is kept).
 */
export function sanitizeFileName(name: string): string {
  const base = name.split(/[/\\]/).pop() ?? "";
  let clean = base
    .normalize("NFC")
    // biome-ignore lint/suspicious/noControlCharactersInRegex: removing control characters
    .replace(/[\u0000-\u001f\u007f[\]()<>"]/g, "")
    .replace(/^[.\s]+/, "")
    .trim();
  if (clean.length > MAX_ATTACHMENT_NAME_LENGTH) {
    const dot = clean.lastIndexOf(".");
    const ext = dot > 0 && clean.length - dot <= 10 ? clean.slice(dot) : "";
    clean = clean.slice(0, MAX_ATTACHMENT_NAME_LENGTH - ext.length) + ext;
  }
  return clean || "file";
}

/** FR1: the Markdown that shows an attachment in a note. */
export function attachmentMarkdown(attachment: { id: string; name: string; mimeType: string }) {
  const link = `[${attachment.name}](${attachmentPath(attachment.id)})`;
  return isInlineImage(attachment.mimeType) ? `!${link}` : link;
}

/** The attachment id in a `/api/attachments/<id>` URL, or null. */
export function attachmentIdOf(url: string): string | null {
  const match = /^\/api\/attachments\/([0-9a-f-]{36})$/.exec(url);
  return match?.[1] ?? null;
}

/** "1.4 MB" style sizes (1024 based). */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}
